// React
import React, { useCallback, useEffect, useState } from "react";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
// MUI
import { Box, Button, Collapse, IconButton, Typography } from "@mui/material";

import { useTranslation } from "react-i18next";
// Third party imports
import { useNavigate } from "react-router-dom";

// Utils
import { APP_ERROR_KIND, classifyAppError } from "utils/errorClassification";

// Assets
import genericErrorImage from "assets/img/generic-error.png";

// Evita loop di reload se la nuova build non risolve il problema
const RELOAD_GUARD_KEY = "app-updated-reload-at";
const RELOAD_GUARD_WINDOW_MS = 60_000;
const AUTO_RELOAD_SECONDS = 5;

const shouldAutoReload = () => {
  try {
    const lastReload = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    return !lastReload || Date.now() - lastReload > RELOAD_GUARD_WINDOW_MS;
  } catch {
    // sessionStorage non disponibile (es. modalita' privata): non forziamo il reload
    return false;
  }
};

const markReload = () => {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // ignoriamo: il reload manuale resta comunque disponibile
  }
};

const ErrorFallback = ({ error, resetErrorBoundary }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const kind = classifyAppError(error);
  const isGeneric = kind === APP_ERROR_KIND.GENERIC;
  const [showDetails, setShowDetails] = useState(isGeneric);
  const [countdown, setCountdown] = useState(
    kind === APP_ERROR_KIND.APP_UPDATED && shouldAutoReload() ? AUTO_RELOAD_SECONDS : null
  );

  const handleRetry = useCallback(() => {
    markReload();
    resetErrorBoundary();
    window.location.reload();
  }, [resetErrorBoundary]);

  // Dopo un rilascio i chunk della vecchia build non esistono piu': ricarichiamo l'app
  useEffect(() => {
    if (countdown === null) return undefined;
    if (countdown <= 0) {
      handleRetry();
      return undefined;
    }
    const timer = setTimeout(() => setCountdown((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, handleRetry]);

  const handleGoHome = () => {
    navigate("/");
    handleRetry();
  };
  const errorDetails = `${t("errors:errorFallback.errorPrefix")} \n${error?.message || ""}`;
  const handleCopyError = () => {
    navigator.clipboard.writeText(errorDetails);
    alert(t("errors:errorFallback.copiedToClipboard"));
  };
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100dvh"
      textAlign="center"
      padding={3}
    >
      <img
        src={genericErrorImage}
        alt={t("errors:errorFallback.imageAlt")}
        style={{ width: "auto", height: "300px" }}
      />
      <Typography variant="h4" component="h1" gutterBottom>
        {t(`errors:errorFallback.${kind}.title`)}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560 }}>
        {t(`errors:errorFallback.${kind}.description`)}
      </Typography>
      {countdown !== null && (
        <Typography variant="body2" color="text.secondary" mt={1}>
          {t("errors:errorFallback.autoReload", { seconds: Math.max(countdown, 0) })}
        </Typography>
      )}
      <Box mt={3}>
        <Button variant="contained" color="primary" onClick={handleRetry} sx={{ mr: 2 }}>
          {t(`errors:errorFallback.${kind}.action`)}
        </Button>
        <Button variant="outlined" color="secondary" onClick={handleGoHome}>
          {t("errors:errorFallback.goHome")}
        </Button>
      </Box>
      <Box mt={2}>
        <Button size="small" color="inherit" onClick={() => setShowDetails((value) => !value)}>
          {showDetails
            ? t("errors:errorFallback.hideDetails")
            : t("errors:errorFallback.showDetails")}
        </Button>
        <Collapse in={showDetails}>
          <Box position="relative" display="inline-block">
            <textarea
              readOnly
              value={errorDetails}
              rows={10}
              className="field m-2.5 w-[min(32rem,calc(100vw-3rem))] text-danger"
            />
            <IconButton
              onClick={handleCopyError}
              sx={{
                position: "absolute",
                top: "5px",
                right: "5px",
                backgroundColor: "white",
                padding: "5px",
              }}
            >
              <ContentCopyIcon />
            </IconButton>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};
export default ErrorFallback;
